import React, {useMemo} from "react"
import {useCookies} from "react-cookie"
import {useTranslation} from "react-i18next"
import {makeStyles} from "@material-ui/core/styles"
import {
  Box,
  Slider,
  Paper,
  RadioGroup,
  Radio,
  Checkbox,
  Grid,
  FormControl,
  FormLabel,
  FormControlLabel,
  Typography,
} from "@material-ui/core"
import clsx from "clsx"
import {useDispatch, useStore} from "../Store"
import {getCookieOptions, isAuth0Stripe} from "../common"
import "../i18n"

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 850,
    margin: `auto`,
    padding: 16,
    [theme.breakpoints.down(`sm`)]: {
      padding: 0,
    },
  },
  form: {
    display: `contents`,
  },
  appInfo: {
    margin: 8,
    lineHeight: 2,
  },
  paper: {
    padding: 16,
  },
  label: {
    fontSize: 20,
    width: `inherit`,
    padding: `${theme.spacing(2)}px 0px ${theme.spacing(1)}px 0px`,
    marginBottom: theme.spacing(1),
    borderBottom: `ridge`,
  },
  slider: {
    margin: 32,
    marginBottom: 8,
    width: `-webkit-fill-available`,
  },
  pcOnly: {
    [theme.breakpoints.down(`xs`)]: {
      display: `none`,
    },
  },
}))

export default function SettingsPage() {
  const classes = useStyles()
  const {
    store: {preset, themeColor, locale, showHeader, showFooter, showNav, sidebarWidth},
  } = useStore()
  const {dispatch} = useDispatch()
  const {t, i18n} = useTranslation()
  const [, setCookie] = useCookies()

  const changePreset = (preset) => {
    dispatch({
      type: `PRESET`,
      preset,
    })
    setCookie(`milizeLayoutPreset`, preset, getCookieOptions())
  }

  const changeSidebarWidth = (sidebarWidth) => {
    dispatch({
      type: `SIDEBAR_WIDTH`,
      sidebarWidth,
    })
    setCookie(`milizeSidebarWidth`, sidebarWidth, getCookieOptions())
  }

  const changeThemeColor = (themeColor) => {
    dispatch({
      type: `THEME_COLOR`,
      themeColor,
    })
    setCookie(`milizeThemeColor`, themeColor, getCookieOptions())
  }

  const changeShowFlag = (target) => ({target: {checked}}) => {
    const key = target.charAt(0).toUpperCase() + target.slice(1)
    dispatch({
      type: `SHOW_${target.toUpperCase()}`,
      [`show${key}`]: checked,
    })
    setCookie(`milizeShow${key}`, checked, getCookieOptions())
  }

  const changeLanguage = async (locale, i18n) => {
    dispatch({
      type: `LOCALE`,
      locale,
    })
    setCookie(`milizeLocale`, locale, getCookieOptions())
    await i18n.changeLanguage(locale)
  }

  const appInfo = useMemo(
    () => (
      <FormControl component={`fieldset`} className={classes.form}>
        <FormLabel className={classes.label} component={`legend`}>
          {t(`appInfo`)}
        </FormLabel>
        <Typography className={classes.appInfo}>{t(`version`) + process.env.APP_VERSION}</Typography>
        <Typography className={classes.appInfo}>{t(`updated`) + process.env.UPDATED}</Typography>
      </FormControl>
    ),
    [classes, preset, locale],
  )
  const presets = useMemo(
    () => (
      <FormControl component={`fieldset`} className={clsx(classes.form, classes.pcOnly)}>
        <FormLabel className={classes.label} component={`legend`}>
          {t(`presets`)}
        </FormLabel>
        <RadioGroup name={`preset`} value={preset} onChange={(e) => changePreset(e.target.value)}>
          <FormControlLabel value={`createDefaultLayout`} control={<Radio />} label={t(`layout.default`)} />
          <FormControlLabel value={`createStandardLayout`} control={<Radio />} label={t(`layout.standard`)} />
          <FormControlLabel value={`createContentBasedLayout`} control={<Radio />} label={t(`layout.contentBased`)} />
          {/* <FormControlLabel value={`createFixedLayout`} control={<Radio />} label={t(`layout.fixed`)} /> */}
          {/* <FormControlLabel value={`createCozyLayout`} control={<Radio />} label={t(`layout.cozy`)} /> */}
          {/* <FormControlLabel value={`createMuiTreasuryLayout`} control={<Radio />} label={t(`layout.treasury`)} /> */}
        </RadioGroup>
      </FormControl>
    ),
    [classes, preset, locale],
  )

  const sidePanelWidth = useMemo(
    () => (
      <FormControl component={`fieldset`} className={clsx(classes.form, classes.pcOnly)}>
        <FormLabel className={classes.label} component={`legend`}>
          {t(`sidebarWidth`)}
        </FormLabel>
        <Box py={8}>
          <Slider
            className={classes.slider}
            min={200}
            max={500}
            value={Number(sidebarWidth)}
            onChange={(e, value) => changeSidebarWidth(value)}
            step={50}
            valueLabelDisplay="on"
          />
        </Box>
      </FormControl>
    ),
    [classes, sidebarWidth],
  )

  const themeColors = useMemo(
    () => (
      <FormControl component={`fieldset`} className={classes.form}>
        <FormLabel className={classes.label} component={`legend`}>
          {t(`themeColor`)}
        </FormLabel>
        <RadioGroup name={`themeColor`} value={themeColor} onChange={(e) => changeThemeColor(e.target.value)}>
          <FormControlLabel value={`dark`} control={<Radio />} label={t(`dark`)} />
          <FormControlLabel value={`light`} control={<Radio />} label={t(`light`)} />
        </RadioGroup>
      </FormControl>
    ),
    [classes, locale],
  )

  const locales = useMemo(
    () => (
      <FormControl component={`fieldset`} className={classes.form}>
        <FormLabel className={classes.label} component={`legend`}>
          {t(`locale`)}
        </FormLabel>
        <RadioGroup aria-label={`locale`} name={`locale`} value={locale} onChange={(e) => changeLanguage(e.target.value, i18n)}>
          <FormControlLabel value={`ja`} control={<Radio />} label={t(`japanese`)} />
          <FormControlLabel value={`en`} control={<Radio />} label={t(`english`)} />
        </RadioGroup>
      </FormControl>
    ),
    [classes, locale],
  )

  const showElements = useMemo(
    () => (
      <FormControl component={`fieldset`} className={classes.form}>
        <FormLabel className={classes.label} component={`legend`}>
          {t(`show`)}
        </FormLabel>
        {/*<FormControlLabel control={<Checkbox checked={showHeader} onChange={changeShowFlag(`header`)} />} label={t(`Header`)} />*/}
        {/*<FormControlLabel control={<Checkbox checked={showNav} onChange={changeShowFlag("nav")} />} label={"nav"} />*/}
        <FormControlLabel control={<Checkbox checked={showFooter} onChange={changeShowFlag(`footer`)} />} label={t(`footer`)} />
      </FormControl>
    ),
    [classes, showHeader, showFooter, showNav, locale],
  )

  return useMemo(
    () => (
      <div className={classes.root}>
        <Paper elevation={1} square className={classes.paper}>
          <Grid container>
            <Grid item xs={12}>
              {appInfo}
              {presets}
              {sidePanelWidth}
              {themeColors}
              {locales}
              {isAuth0Stripe() && showElements}
            </Grid>
          </Grid>
        </Paper>
      </div>
    ),
    [classes, presets, preset, sidePanelWidth, themeColors, locales, showElements],
  )
}
